import MojitoServices from 'mojito/services';
import { upperFirst } from 'mojito/utils';

const BetsTypes = MojitoServices.Bets.types;

const { OPEN, WON, LOST, VOID, CASHED_OUT } = BetsTypes.BET_STATUS;

/**
 * A utility class specifically designed for use within the `BetView` component.
 *
 * @class BetViewUtils
 * @name betViewUtils
 * @memberof Mojito.Modules.OpenBets
 */
export default class BetViewUtils {
    /**
     * Returns the corresponding leg/bet status icon config for a leg.
     *
     * @param {string} status - The status of the leg/bet.
     * @param {boolean} earlyPayoutAwarded - True if early payout is awarded.
     *
     * @returns {string} The config status for the leg/bet icon.
     * @function Mojito.Modules.OpenBets.betViewUtils.getStatusIcon
     */
    static getStatusIcon(status, earlyPayoutAwarded = false) {
        switch (status) {
            case OPEN:
                // Exceptional case for early payout awarded.
                // When the leg status is still open and early payout is awarded it should show as won.
                return earlyPayoutAwarded ? 'won' : 'open';
            case VOID:
                return 'void';
            case WON:
                return 'won';
            case LOST:
                return 'lost';
            case CASHED_OUT:
                return 'cashedOut';
        }
    }

    /**
     * Returns the config key for a bet status text.
     *
     * @param {string} betStatus - The status of the leg/bet.
     *
     * @returns {string} The config key for the leg/bet status text.
     * @function Mojito.Modules.OpenBets.betViewUtils.getStatusTextKey
     */
    static getStatusTextKey(betStatus) {
        return upperFirst(BetViewUtils.getStatusIcon(betStatus));
    }

    /**
     * Get bet's part with earlyPayout functionality.
     *
     * @param {Mojito.Services.Bets.Types.Leg} leg - Leg of the bet.
     *
     * @returns  {Mojito.Services.Betslip.types.Part} Betslip bet part.
     * @function Mojito.Modules.OpenBets.betViewUtils.getEarlyPayoutPart
     */
    static getEarlyPayoutPart(leg) {
        return leg?.parts?.find(part => part.earlyPayout);
    }

    /**
     * Check if early payout awarded.
     *
     * @param {Mojito.Services.Bets.Types.Part} part - Part of the leg.
     *
     * @returns  {boolean} returns true when early payout is awarded.
     * @function Mojito.Modules.OpenBets.betViewUtils.isEarlyPayoutAwarded
     */
    static isEarlyPayoutAwarded(part) {
        return !!part?.earlyPayout?.awarded;
    }

    /**
     * Get early payout rules from part.
     *
     * @param {Mojito.Services.Bets.Types.Part} part - Part of the leg.
     *
     * @returns  {Array} returns array of rules as key value pairs.
     * @function Mojito.Modules.OpenBets.betViewUtils.getEarlyPayoutRules
     */
    static getEarlyPayoutRules(part) {
        return part?.earlyPayout?.rules || [];
    }

    /**
     * Check if bet is a single bet.
     *
     * @param {Mojito.Services.Bets.Types.Bet} bet - A single bet.
     *
     * @returns  {boolean} returns true when it is a single bet.
     * @function Mojito.Modules.OpenBets.betViewUtils.isSingleBet
     */
    static isSingleBet(bet) {
        return (
            bet.betType === BetsTypes.BET_TYPE.SINGLE &&
            bet.legs[0].legSort === BetsTypes.LEG_SORT.DEFAULT
        );
    }
}
